@use '@/styles/tokens' as *;

/* The Field Mapping panel's Co-ordinates section (Figma 594:532573 body, 594:532582 rows region).
 *
 * A flex column that fills the panel's body, so the ROWS scroll and the header, the Add point
 * control and the Cancel/Save actions stay in view — which is the criterion, and the reason
 * `field-mapping-panel.module.scss` hands its overflow down to this child. */
.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-height: 0;
  height: 100%;
}

/* Figma 594:532574 — the heading row and the helper line, 8 apart. */
.header {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.headingRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Figma leaf 594:532576 — Geist 13/M. The fill is unbound raw black; snapped to the near-black
 * semantic token, as every shipped module does. */
.heading {
  margin: 0;
  font-size: $font-size-13;
  font-weight: $font-weight-medium;
  color: var(--text-primary);
}

/* Figma 839:553493 — a bordered chip, NOT a <Button>: the shipped button tokens are a 32px pill at
 * 11px, and this is a 24px 6px-radius control at 12px. */
.addPoint {
  @include reset-button;
  @include focus-ring;

  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--border-default);
  border-radius: $radius-md;
  font-size: $font-size-12;
  font-weight: $font-weight-regular;
  color: var(--text-primary);
  cursor: pointer;

  /* At the 50-row cap. Undesigned; built from the shipped disabled colourway. */
  &:disabled {
    border-color: var(--border-subtle);
    color: var(--text-disabled);
    cursor: not-allowed;
  }
}

/* Figma leaf 594:532581 — Geist 11/R, Text/Tertiary. */
.helper {
  margin: 0;
  font-size: $font-size-11;
  font-weight: $font-weight-regular;
  color: var(--text-tertiary);
}

/* THE scroller. `min-height: 0` is what lets a flex child shrink below its content and actually
 * overflow; without it the list grows and pushes the foot out of the panel. */
.rows {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.row {
  display: flex;
  flex-direction: column;
  /* Figma 6 — off the --space-* scale, written raw for the same reason marked-points.module.scss
   * writes its own 21px/10.5px: snapping it to 4 or 8 would move the row's height off the frame. */
  gap: 6px;
}

/* Figma 594:533214 (a Point's number) and 598:540032 / 598:540039 (a circle's "Mid Point" and
 * "Radius") are the same leaf — px-5, Geist 12/R, Text/Primary. Two names for one rule rather than
 * one name used loosely. */
.pointLabel,
.groupLabel {
  margin: 0;
  padding-inline: var(--space-1);
  font-size: $font-size-12;
  font-weight: $font-weight-regular;
  color: var(--text-primary);
}

/* Figma 594:533216 — Lat, Long and (above three rows) the delete control, 12 apart. The two cells
 * share what the delete control leaves, which IS D7's "rows widen at exactly three": no control,
 * no 32px to give up. */
.inputs {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.cell {
  flex: 1;
  min-width: 0;
}

/* Figma leaf I594:533217;118:692 — the VALUE is Geist 14/M; antd's default is regular. Scoped from
 * `.cell` through `:global`, NOT via a `className` on `<Input>`: `allowClear` wraps the control in an
 * affix wrapper, and antd routes `className` to the wrapper rather than the inner `<input>` when it
 * does, so a class on the component would style the box and miss the text.
 *
 * The box itself needs nothing — theme.ts's Input tokens already carry 40px / radius 10 /
 * Border-Subtle / Error-900. */
.cell :global(.ant-input) {
  font-weight: $font-weight-medium;
}

/* Figma 594:532926 shows the error ring on an UNFOCUSED input; antd applies `errorActiveShadow` only
 * while focused, so the resting state is drawn here from the same token. Both selectors, because the
 * status class lands on the affix wrapper when `allowClear` is on and on the input itself when it is
 * not — and which one antd picks is not this stylesheet's business to predict. */
.cell :global(.ant-input-affix-wrapper-status-error):not(:focus-within),
.cell :global(.ant-input-status-error):not(:focus) {
  box-shadow: var(--focus-field-error);
}

/* Figma leaf I594:532926;118:613 — Geist 11/L in Error/900, one line, ellipsised. Truncating rather
 * than wrapping is the frame's own choice, and it keeps a row's height stable as messages change.
 * `$font-weight-light` does not exist in this repo's type scale (only regular/medium/semibold/bold
 * are generated) — `$font-weight-regular` is the nearest shipped token. */
.error {
  margin: var(--space-1) 0 0;
  overflow: hidden;
  font-size: $font-size-11;
  font-weight: $font-weight-regular;
  color: $color-error-900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Figma 594:533219 — Trash 20px in Error/900. */
.delete {
  @include reset-button;
  @include focus-ring;

  display: flex;
  align-items: center;
  /* Centres the 20px glyph against the 40px input beside it. */
  height: 40px;
  color: $color-error-900;
  cursor: pointer;
}

/* Figma 594:532618 — two equal buttons, 8 apart, pinned below the scroller. */
.foot {
  display: flex;
  gap: var(--space-2);
}

.action {
  flex: 1;
}

/* Figma leaf I598:540041;118:542 — the Radius input's "Ft", Geist 13/R. Its fill is unbound raw
 * `#0E0901`; snapped to the near-black semantic token, the same call `.heading` above documents.
 * antd's affix wrapper owns the 15px inset, so there is no positioning here.
 *
 * It is a LABEL, never input: the criterion is that the suffix is not editable and not part of the
 * value, and rendering it outside the control is what makes that structural rather than a rule
 * something has to strip. */
.suffix {
  font-size: $font-size-13;
  font-weight: $font-weight-regular;
  color: var(--text-primary);
}
